import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";

/** Junta classes ignorando falsos, sem trazer dependência para isso. */
export function cx(...partes: (string | false | null | undefined)[]): string {
  return partes.filter(Boolean).join(" ");
}

type Tom = "brasa" | "quieto" | "descartar" | "fantasma";

const TONS: Record<Tom, string> = {
  // Âmbar = ação do operador. É o único preenchimento sólido do sistema,
  // e por isso só pode haver um por tela.
  brasa: "bg-brasa text-fundo hover:bg-brasa-forte active:bg-brasa-forte font-semibold",
  quieto: "bg-superficie-alta text-texto hover:bg-borda border border-borda",
  descartar: "bg-transparent text-descartar border border-descartar/50 hover:bg-descartar/10",
  fantasma: "bg-transparent text-texto-suave hover:text-texto hover:bg-superficie",
};

export interface BotaoProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Para quem precisa devolver o foco ao botão (a tela que fecha e o botão que a abriu). */
  ref?: Ref<HTMLButtonElement>;
  tom?: Tom;
  largo?: boolean;
  carregando?: boolean;
  /**
   * `nu` remove o preenchimento e alinha à esquerda, para ações que vivem numa
   * lista já indentada.
   *
   * É uma variante e não uma sobrescrita via `className` de propósito: `px-0`
   * passado de fora briga com o `px-4` da base, e com especificidade igual quem
   * vence é a ordem no CSS gerado — resultado imprevisível.
   */
  formato?: "padrao" | "nu";
}

export function Botao({
  tom = "quieto",
  largo,
  carregando,
  formato = "padrao",
  disabled,
  children,
  className,
  ...props
}: BotaoProps) {
  return (
    <button
      {...props}
      disabled={disabled || carregando}
      aria-busy={carregando || undefined}
      className={cx(
        "inline-flex items-center gap-2 rounded-peca",
        formato === "nu" ? "justify-start px-0 py-2" : "justify-center px-4 py-3",
        "text-[0.9375rem] leading-none tracking-[-0.01em] transition-colors",
        // Desabilitado vira cinza de verdade: âmbar a 45% de opacidade ainda lê
        // como clicável, e o operador tenta tocar.
        "disabled:cursor-not-allowed disabled:border-borda disabled:bg-superficie",
        "disabled:text-texto-fraco disabled:shadow-none",
        largo && "w-full",
        TONS[tom],
        className,
      )}
    >
      {carregando && <Girando />}
      {children}
    </button>
  );
}

export function Girando({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cx(
        "size-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent",
        className,
      )}
    />
  );
}

export function Campo({
  rotulo,
  erro,
  dica,
  children,
}: {
  rotulo: string;
  erro?: string | null;
  dica?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[0.8125rem] font-medium text-texto-suave">{rotulo}</span>
      {children}
      {erro ? (
        <span className="mt-1.5 block text-[0.8125rem] text-descartar">{erro}</span>
      ) : dica ? (
        <span className="mt-1.5 block text-[0.8125rem] text-texto-fraco">{dica}</span>
      ) : null}
    </label>
  );
}

export function Entrada({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cx(
        "w-full rounded-peca border border-borda bg-superficie px-3 py-3",
        // 16px evita o zoom automático do iOS ao focar um campo.
        "text-base text-texto placeholder:text-texto-fraco",
        "transition-colors focus:border-brasa focus:outline-none",
        className,
      )}
    />
  );
}

/**
 * Campo de texto longo.
 *
 * `field-sizing-content` faz a caixa crescer com o conteúdo até o limite; onde o
 * navegador não suporta, o `min-h` garante um tamanho útil e a alça de
 * redimensionar continua disponível.
 */
export function Area({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={cx(
        "w-full resize-y rounded-peca border border-borda bg-superficie px-3 py-3",
        "field-sizing-content max-h-[28rem] min-h-[7rem]",
        // 16px evita o zoom automático do iOS ao focar o campo.
        "text-base leading-relaxed text-texto placeholder:text-texto-fraco",
        "transition-colors focus:border-brasa focus:outline-none",
        className,
      )}
    />
  );
}

/**
 * Interruptor.
 *
 * `role="switch"` em vez de checkbox estilizado: o leitor de tela anuncia
 * "ativado/desativado", que é o que o controle significa, e não "marcado".
 */
export function Chave({
  ligada,
  aoAlternar,
  rotulo,
  texto,
  descricaoId,
  disabled,
}: {
  ligada: boolean;
  aoAlternar: (ligada: boolean) => void;
  rotulo: string;
  /**
   * O que aparece ao lado do interruptor. Sem ele, "Ativado"/"Desativado". Com ele, o estado já está
   * dito pelo próprio interruptor e o texto é só o nome do que ele liga — "Área útil do papel", e não
   * "Área útil do papel: ativado".
   */
  texto?: string;
  /** O `id` de um texto que explica o interruptor, para o leitor de tela ler junto do nome. */
  descricaoId?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={ligada}
      aria-label={rotulo}
      aria-describedby={descricaoId}
      disabled={disabled}
      onClick={() => aoAlternar(!ligada)}
      className={cx(
        // 44px de alvo de toque, o mínimo para o dedo, mesmo com o trilho menor.
        "inline-flex min-h-11 items-center gap-3 disabled:cursor-not-allowed disabled:opacity-60",
      )}
    >
      <span
        className={cx(
          "relative h-6 w-11 shrink-0 rounded-full transition-colors",
          ligada ? "bg-brasa" : "bg-borda",
        )}
      >
        <span
          className={cx(
            "absolute top-0.5 size-5 rounded-full bg-fundo transition-[left]",
            ligada ? "left-[1.375rem]" : "left-0.5",
          )}
        />
      </span>
      <span className="text-[0.9375rem] text-texto">
        {texto ?? (ligada ? "Ativado" : "Desativado")}
      </span>
    </button>
  );
}

/**
 * Lista suspensa.
 *
 * A seta é desenhada como imagem de fundo porque `appearance-none` remove a
 * nativa, e sem isso o campo fica indistinguível de uma `Entrada`. O `pr-10`
 * reserva o espaço dela.
 */
export function Selecao({
  className,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={cx(
        "w-full appearance-none rounded-peca border border-borda bg-superficie py-3 pr-10 pl-3",
        // 16px evita o zoom automático do iOS ao focar o campo.
        "text-base text-texto",
        "transition-colors focus:border-brasa focus:outline-none",
        "disabled:cursor-not-allowed disabled:text-texto-fraco",
        "bg-[length:1.25rem] bg-[right_0.625rem_center] bg-no-repeat",
        "bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke%3D%22%238a8178%22%20stroke-width%3D%221.5%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20d%3D%22m6%209%206%206%206-6%22/%3E%3C/svg%3E')]",
        className,
      )}
    >
      {children}
    </select>
  );
}

/**
 * Escolha de uma entre poucas opções lado a lado — "Peça com capa | Foto da IA". Cada opção é um botão
 * com `aria-pressed`, e a escolhida leva a tinta fraca da brasa.
 *
 * Com 44 px de altura: serve ao dedo, que é onde o visor e a tela de escolha de fotos mais a usam.
 */
export function Segmentos<T extends string>({
  rotulo,
  valor,
  opcoes,
  aoMudar,
  className,
}: {
  /** O que a escolha decide, para o leitor de tela anunciar o grupo. */
  rotulo: string;
  valor: T;
  opcoes: readonly { id: T; rotulo: string }[];
  aoMudar: (id: T) => void;
  className?: string;
}) {
  return (
    <div
      role="group"
      aria-label={rotulo}
      className={cx(
        "inline-flex max-w-full rounded-peca border border-borda bg-superficie p-0.5",
        className,
      )}
    >
      {opcoes.map((o) => (
        <button
          key={o.id}
          type="button"
          aria-pressed={valor === o.id}
          onClick={() => aoMudar(o.id)}
          className={cx(
            "min-h-11 min-w-0 rounded-[calc(var(--radius-peca)-2px)] px-3.5 text-[0.875rem] transition-colors",
            valor === o.id ? "bg-brasa-fraca text-brasa" : "text-texto-suave hover:text-texto",
          )}
        >
          {o.rotulo}
        </button>
      ))}
    </div>
  );
}

/** Etiqueta curta de estado. O texto carrega o significado, a cor reforça. */
export function Selo({
  tom,
  children,
  className,
}: {
  tom: "brasa" | "projecao" | "fixado" | "descartar" | "quieto";
  children: ReactNode;
  className?: string;
}) {
  const cores = {
    brasa: "bg-brasa-fraca text-brasa",
    projecao: "bg-projecao-fraca text-projecao",
    fixado: "bg-fixado/15 text-fixado",
    descartar: "bg-descartar/15 text-descartar",
    quieto: "bg-superficie-alta text-texto-suave",
  } as const;

  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1",
        "text-[0.75rem] font-medium tracking-[0.01em]",
        cores[tom],
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * Estado vazio.
 *
 * Tela vazia é convite para agir, não recado de que não há nada. Por isso a ação
 * é obrigatória quando existe uma.
 */
export function Vazio({
  titulo,
  descricao,
  acao,
  compacto = false,
}: {
  titulo: string;
  descricao: string;
  acao?: ReactNode;
  /** Dentro de um bloco de formulário, onde a página inteira vazia seria exagero. */
  compacto?: boolean;
}) {
  return (
    <div
      className={cx(
        "flex flex-col items-center gap-3 px-6 text-center",
        compacto ? "py-8" : "py-14",
      )}
    >
      <p className="font-display text-[1.375rem] leading-tight text-texto">{titulo}</p>
      <p className="max-w-[34ch] text-[0.9375rem] leading-relaxed text-texto-suave">{descricao}</p>
      {acao && <div className="mt-2">{acao}</div>}
    </div>
  );
}

/**
 * Falha.
 *
 * Diz o que aconteceu e o que fazer. Nunca pede desculpa, nunca é vago.
 */
export function Falha({
  mensagem,
  tentarNovamente,
}: {
  mensagem: string;
  tentarNovamente?: () => void;
}) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-peca border border-descartar/40 bg-descartar/10 px-4 py-3"
    >
      <p className="min-w-0 flex-1 text-[0.9375rem] leading-snug text-texto wrap-anywhere">
        {mensagem}
      </p>
      {tentarNovamente && (
        <button
          onClick={tentarNovamente}
          className="shrink-0 text-[0.875rem] font-medium text-descartar underline underline-offset-2"
        >
          Tentar de novo
        </button>
      )}
    </div>
  );
}
